import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import moment from "moment";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";
import { Search, X } from "lucide-react";

import ClubbieModal from "../Modal/ClubbieModal";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import DatePicker from "../DatePicker/DatePicker";
import { updateTournament, getSelectedTournament, uploadTournamentLogo } from "../../redux/slices/tournamentSlice";
import uploadIconSvg from "../../assets/images/icons/upload-icon.svg";
import SPORT_TYPES from "../../utils/sportTypes";

const INPUT_CLASS =
  "w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors";

const LABEL_CLASS = "block text-[14px] font-normal text-clubbie-white";

const MAP_STYLES = [
  { featureType: "all", elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { featureType: "all", elementType: "labels.text.stroke", stylers: [{ lightness: -80 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#263c3f" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6b9a76" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f2835" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#f3d19c" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f3948" }] },
  { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#515c6d" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ lightness: -20 }] },
];

const STEP_LABELS = ["Details", "Configuration", "Venue"];

/**
 * EditCompetitionModal — Multi-step edit competition wizard.
 */
const EditCompetitionModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { selectedTournament } = useSelector((state) => state.tournament);
  const { selectedOrganisation } = useSelector((state) => state.organisation);

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Details
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Configuration
  const [sport, setSport] = useState("");
  const [extraSport, setExtraSport] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState("");
  const [editingCategoryIdx, setEditingCategoryIdx] = useState(null);
  const [editingCategoryValue, setEditingCategoryValue] = useState("");

  // Venue
  const [venueName, setVenueName] = useState("");
  const [location, setLocation] = useState(null);
  const [mapLink, setMapLink] = useState("");
  const [locationName, setLocationName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Sport dropdown
  const [isSportDropdownOpen, setIsSportDropdownOpen] = useState(false);
  const [sportSearch, setSportSearch] = useState("");
  const [sportDropdownPos, setSportDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const sportTriggerRef = useRef(null);
  const sportDropdownRef = useRef(null);

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const sports = [
    { id: SPORT_TYPES.FOOTBALL, name: "Football" },
    { id: SPORT_TYPES.RUGBY, name: "Rugby" },
    { id: SPORT_TYPES.TOUCH_RUGBY, name: "Touch Rugby" },
    { id: "other", name: "Other" },
  ];

  const primarySportIds = [SPORT_TYPES.FOOTBALL, SPORT_TYPES.RUGBY, SPORT_TYPES.TOUCH_RUGBY, SPORT_TYPES.UNIVERSAL];
  const otherSports = Object.values(SPORT_TYPES)
    .filter((v) => !primarySportIds.includes(v))
    .map((v) => ({ id: v, name: v.charAt(0).toUpperCase() + v.slice(1) }));
  const filteredOtherSports = otherSports.filter((s) =>
    s.name.toLowerCase().includes(sportSearch.toLowerCase())
  );

  const updateSportDropdownPosition = () => {
    if (!sportTriggerRef.current) return;
    const rect = sportTriggerRef.current.getBoundingClientRect();
    setSportDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  // Close sport dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        sportTriggerRef.current && !sportTriggerRef.current.contains(e.target) &&
        sportDropdownRef.current && !sportDropdownRef.current.contains(e.target)
      ) {
        setIsSportDropdownOpen(false);
        setSportSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update sport dropdown portal position on scroll/resize
  useEffect(() => {
    if (!isSportDropdownOpen) return;
    updateSportDropdownPosition();
    window.addEventListener("scroll", updateSportDropdownPosition, true);
    window.addEventListener("resize", updateSportDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateSportDropdownPosition, true);
      window.removeEventListener("resize", updateSportDropdownPosition);
    };
  }, [isSportDropdownOpen]);

  // Prefill when modal opens
  useEffect(() => {
    if (!isOpen || !selectedTournament) return;
    setCurrentStep(0);
    setIsSportDropdownOpen(false);
    setSportSearch("");

    setName(selectedTournament.name || "");
    setLogoUrl(selectedTournament.logo || "");
    setLogoFile(null);
    setLogoPreview(null);

    if (selectedTournament.matchSchedule?.startDate) {
      setStartDate(moment(selectedTournament.matchSchedule.startDate).format("YYYY-MM-DD"));
    }
    if (selectedTournament.matchSchedule?.endDate) {
      setEndDate(moment(selectedTournament.matchSchedule.endDate).format("YYYY-MM-DD"));
    }

    const s = selectedTournament.sport || "";
    const knownSports = [SPORT_TYPES.FOOTBALL, SPORT_TYPES.RUGBY, SPORT_TYPES.TOUCH_RUGBY];
    if (knownSports.includes(s)) {
      setSport(s);
      setExtraSport("");
    } else if (s) {
      setSport("other");
      setExtraSport(s);
    }

    setCategories(selectedTournament.categories || []);
    setCategoryInput("");
    setEditingCategoryIdx(null);

    setVenueName(selectedTournament.matchVenue?.venue || "");
    const loc = selectedTournament.matchVenue?.location;
    if (loc?.lat && loc?.lng) {
      setLocation({ lat: loc.lat, lng: loc.lng });
    }
    setMapLink(selectedTournament.matchVenue?.mapLink || "");
    setSearchQuery("");
    setLocationName(selectedTournament.matchVenue?.address || "");
  }, [isOpen, selectedTournament]);

  // Fallback for tournaments saved before matchVenue.address existed: reverse-
  // geocode the stored lat/lng so the location field shows the same address as
  // the map pin. A ref dedups so we don't refire on every render — without it
  // the locationName-dependency had a closure race with the prefill setter.
  const reverseGeocodedForRef = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      reverseGeocodedForRef.current = null;
      return;
    }
    if (selectedTournament?.matchVenue?.address) return;
    if (!isMapsLoaded || !window.google?.maps?.Geocoder) return;
    const loc = selectedTournament?.matchVenue?.location;
    if (!loc?.lat || !loc?.lng) return;

    const key = `${selectedTournament?.id || selectedTournament?._id}:${loc.lat},${loc.lng}`;
    if (reverseGeocodedForRef.current === key) return;
    reverseGeocodedForRef.current = key;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat: loc.lat, lng: loc.lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setLocationName(results[0].formatted_address);
      }
    });
  }, [isOpen, selectedTournament, isMapsLoaded]);

  // Logo handlers
  const handleFileChange = (file) => {
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/svg+xml", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload PNG, JPG, SVG or GIF.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size too large. Maximum size is 5MB.");
      return;
    }
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      setLogoFile(file);
      setLogoPreview(img.src);
    };
    img.onerror = () => toast.error("Invalid image file.");
  };

  // Category handlers
  const handleAddCategory = () => {
    const trimmed = categoryInput.trim();
    if (trimmed && !categories.some((c) => (c.label || c) === trimmed)) {
      setCategories([...categories, { label: trimmed }]);
      setCategoryInput("");
    }
  };

  const handleRemoveCategory = (cat) => {
    const label = cat.label || cat;
    setCategories(categories.filter((c) => (c.label || c) !== label));
  };

  const startEditCategory = (idx) => {
    setEditingCategoryIdx(idx);
    setEditingCategoryValue(categories[idx]?.label || categories[idx] || "");
  };

  const saveEditCategory = () => {
    const trimmed = editingCategoryValue.trim();
    if (trimmed && editingCategoryIdx !== null) {
      setCategories((prev) => {
        const updated = [...prev];
        updated[editingCategoryIdx] = { ...updated[editingCategoryIdx], label: trimmed };
        return updated;
      });
    }
    setEditingCategoryIdx(null);
    setEditingCategoryValue("");
  };

  const cancelEditCategory = () => {
    setEditingCategoryIdx(null);
    setEditingCategoryValue("");
  };

  // Map handlers
  const onPlaceSelected = (place) => {
    if (!place.geometry || !place.geometry.location) return;
    const loc = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    const link = `https://www.google.com/maps?q=${loc.lat},${loc.lng}`;
    setLocation(loc);
    setMapLink(link);
    setSearchQuery(place.formatted_address);
    setLocationName(place.formatted_address);
    if (mapRef.current) {
      mapRef.current.panTo(loc);
      mapRef.current.setZoom(15);
    }
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const loc = { lat, lng };
    const link = `https://www.google.com/maps?q=${lat},${lng}`;
    setLocation(loc);
    setMapLink(link);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: loc }, (results, status) => {
      if (status === "OK" && results[0]) {
        setLocationName(results[0].formatted_address);
      }
    });
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (!endDate || moment(date).isAfter(moment(endDate))) {
      setEndDate(date);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Competition name is required");
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading("Saving changes...");

    try {
      const tournamentId = selectedTournament?.id || selectedTournament?._id;

      // Upload logo if changed
      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        await dispatch(uploadTournamentLogo({ tournamentId, file: formData })).unwrap();
      }

      const resolvedSport = sport === "other" ? extraSport : sport;

      const updateData = {
        name: name.trim(),
        sport: resolvedSport,
        // Strip any populated subtrees (teams/members) that redux may have hydrated
        // onto each category — schema only persists { _id, label }.
        categories: categories.map((c) => ({
          ...(c._id ? { _id: c._id } : {}),
          label: c.label || c,
        })),
        matchSchedule: {
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
        matchVenue: {
          venue: venueName,
          numberOfPitches: selectedTournament?.matchVenue?.numberOfPitches || 1,
          location: location || selectedTournament?.matchVenue?.location || {},
          address: locationName || selectedTournament?.matchVenue?.address || "",
          mapLink: mapLink || selectedTournament?.matchVenue?.mapLink || "",
        },
      };

      await dispatch(updateTournament({ id: tournamentId, updateData })).unwrap();

      if (selectedOrganisation?.id) {
        dispatch(getSelectedTournament(selectedOrganisation.id));
      }

      toast.dismiss(loadingToast);
      toast.success("Competition updated");
      onClose();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error?.message || "Failed to update competition");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLastStep = currentStep === STEP_LABELS.length - 1;
  const displayLogo = logoPreview || logoUrl;

  // Footer
  const footer = (
    <div className="flex items-center gap-3 w-full">
      <button
        type="button"
        className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
        onClick={onClose}
      >
        Cancel
      </button>
      <div className="ml-auto flex items-center gap-3">
        {currentStep > 0 && (
          <button
            type="button"
            className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
            onClick={() => setCurrentStep(currentStep - 1)}
          >
            Back
          </button>
        )}
        {isLastStep ? (
          <button
            type="button"
            className="bg-[#00E7FF] hover:opacity-50 text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? <><LoadingSpinner size="small" /><span>Saving...</span></> : "Save"}
          </button>
        ) : (
          <button
            type="button"
            className="bg-[#00E7FF] hover:opacity-50 text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer"
            onClick={() => setCurrentStep(currentStep + 1)}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );

  return (
    <ClubbieModal
      isOpen={isOpen}
      width="600px"
      maxHeight="85vh"
      toggle={onClose}
      title="Edit competition"
      footer={footer}
    >
      {/* Clickable Stepper */}
      <div className="flex items-center gap-3 mb-5">
        {STEP_LABELS.map((label, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setCurrentStep(idx)}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                idx <= currentStep
                  ? "bg-[#00E7FF] text-[#000F1F]"
                  : "bg-[#192634] text-[#7F8585]"
              }`}
            >
              {idx < currentStep ? (
                <svg className="w-3 h-3" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                idx + 1
              )}
            </div>
            <span className={`text-[13px] font-medium transition-colors ${idx === currentStep ? "text-[#FEFCEB]" : "text-[#7F8585]"}`}>
              {label}
            </span>
            {idx < STEP_LABELS.length - 1 && <div className="w-4 h-px bg-[#192634]" />}
          </div>
        ))}
      </div>

      <div className="space-y-4 overflow-y-auto">
        {/* ==================== STEP 0: DETAILS ==================== */}
        {currentStep === 0 && (
          <div className="flex flex-col gap-4">
            {/* Image Upload */}
            <div className="flex flex-col gap-2">
              <label className={LABEL_CLASS}>Image</label>
              <div
                className={`bg-[#000F1F] border border-[#192634] border-dashed rounded-[24px] p-[30px] flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragOver ? "border-[#00E7FF]" : ""}`}
                onDrop={(e) => { e.preventDefault(); setIsDragOver(false); handleFileChange(e.dataTransfer.files[0]); }}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
                onClick={() => document.getElementById("logo-upload-demo").click()}
              >
                {displayLogo ? (
                  <div className="relative w-full h-full flex items-center justify-center">
                    <img src={displayLogo} alt="Preview" className="w-[50px] h-auto object-contain rounded-md" />
                    <button
                      onClick={(e) => { e.stopPropagation(); setLogoFile(null); setLogoPreview(null); setLogoUrl(""); }}
                      className="absolute top-[-15px] right-[-10px] bg-red-500 rounded-full p-1 hover:bg-red-600 text-white transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-[8px] items-center w-full">
                    <div className="w-14 h-14 rounded-full bg-[#051C33] flex items-center justify-center">
                      <img src={uploadIconSvg} alt="Upload" className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <p className="text-[#FEFCEB] text-sm font-semibold">Upload file</p>
                      <p className="text-app-text-secondary text-xs font-normal">
                        Drag & drop or <span className="text-[#429ABF] underline">choose file</span> to upload
                      </p>
                    </div>
                  </div>
                )}
                <input id="logo-upload-demo" type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e.target.files[0])} />
              </div>
            </div>

            {/* Competition Name */}
            <div className="flex flex-col gap-2">
              <label className={LABEL_CLASS}>Competition name</label>
              <input
                type="text"
                placeholder="Competition name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={INPUT_CLASS}
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <label className={LABEL_CLASS}>Start date</label>
                <DatePicker value={startDate} onChange={handleStartDateChange} disablePastDates={false} />
              </div>
              <div className="flex flex-col gap-2">
                <label className={LABEL_CLASS}>End date</label>
                <DatePicker value={endDate} onChange={(date) => setEndDate(date)} minDate={startDate} disablePastDates={false} />
              </div>
            </div>
          </div>
        )}

        {/* ==================== STEP 1: CONFIGURATION ==================== */}
        {currentStep === 1 && (
          <div className="flex flex-col gap-4">
            {/* Sport */}
            <div className="flex flex-col gap-3">
              <label className={LABEL_CLASS}>Select sport</label>
              <div className="flex flex-col gap-3">
                {sports.filter((s) => s.id !== "other").map((s) => (
                  <label key={s.id} className="flex items-center gap-3 cursor-pointer">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${sport === s.id ? "border-[#00E7FF]" : "border-[#333E48]"}`}>
                      {sport === s.id && <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />}
                    </div>
                    <input type="radio" className="hidden" checked={sport === s.id} onChange={() => { setSport(s.id); setExtraSport(""); }} />
                    <span className="text-sm text-[#FEFCEB]">{s.name}</span>
                  </label>
                ))}
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${sport === "other" || extraSport ? "border-[#00E7FF]" : "border-[#333E48]"}`}>
                    {(sport === "other" || extraSport) && <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />}
                  </div>
                  <input type="radio" className="hidden" checked={sport === "other" || !!extraSport} onChange={() => setSport("other")} />
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[#FEFCEB]">Other</span>
                    {(sport === "other" || extraSport) && (
                      <div className="w-[280px]" ref={sportTriggerRef}>
                        <div
                          className={`flex items-center justify-between h-11 px-4 bg-[#021529] border rounded-full cursor-pointer transition-colors ${isSportDropdownOpen ? "border-[#00E7FF]" : "border-transparent"}`}
                          onClick={() => setIsSportDropdownOpen(!isSportDropdownOpen)}
                        >
                          <span className={`text-[15px] ${extraSport ? "text-[#FEFCEB]" : "text-[#7F8585]"}`}>
                            {extraSport ? extraSport.charAt(0).toUpperCase() + extraSport.slice(1) : "Select a sport..."}
                          </span>
                          <svg className={`w-4 h-4 text-[#FEFCEB] transition-transform ${isSportDropdownOpen ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="none">
                            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>

                        {isSportDropdownOpen && createPortal(
                          <div
                            ref={sportDropdownRef}
                            className="fixed bg-[#000F1F] border border-[#192634] rounded-[20px] shadow-lg overflow-hidden z-[1000]"
                            style={{ top: sportDropdownPos.top, left: sportDropdownPos.left, width: sportDropdownPos.width }}
                          >
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                <Search size={16} />
                              </div>
                              <input
                                type="text"
                                className="w-full h-10 bg-transparent text-[#FEFCEB] text-sm outline-none placeholder-[#7F8585] pl-9 pr-4"
                                placeholder="Search"
                                value={sportSearch}
                                onChange={(e) => setSportSearch(e.target.value)}
                                autoFocus
                              />
                            </div>
                            <div className="max-h-[200px] overflow-y-auto border-t border-[#192634]">
                              {filteredOtherSports.length > 0 ? (
                                filteredOtherSports.map((s) => (
                                  <div
                                    key={s.id}
                                    className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors border-b border-[#192634] last:border-b-0 hover:bg-app-hover ${
                                      extraSport === s.id ? "bg-app-hover" : ""
                                    }`}
                                    onClick={() => {
                                      setExtraSport(s.id);
                                      setSport("other");
                                      setIsSportDropdownOpen(false);
                                      setSportSearch("");
                                    }}
                                  >
                                    <span className={`text-sm font-medium ${extraSport === s.id ? "text-[#00E7FF]" : "text-[#FEFCEB]"}`}>{s.name}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="px-3 py-3 text-[#7F8585] text-sm">No sports found</div>
                              )}
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Categories */}
            <div className="flex flex-col gap-3">
              <label className={LABEL_CLASS}>{"Competition categories (e.g. U12, U14, Senior)"}</label>
              <div className="flex flex-col gap-2">
                <div className="relative">
                  <input
                    type="text"
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    placeholder="Add a category and click add"
                    className={`${INPUT_CLASS} pr-[60px]`}
                    onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
                  />
                  <button
                    onClick={handleAddCategory}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-app-text-secondary text-sm font-medium underline cursor-pointer hover:text-[#FEFCEB] transition-colors"
                  >
                    Add
                  </button>
                </div>
                {categories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat, idx) => (
                      <div key={idx} className="bg-[#082441] px-2 py-1 rounded-full flex items-center gap-1">
                        {editingCategoryIdx === idx ? (
                          <input
                            autoFocus
                            type="text"
                            value={editingCategoryValue}
                            onChange={(e) => setEditingCategoryValue(e.target.value)}
                            onBlur={saveEditCategory}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); saveEditCategory(); }
                              else if (e.key === "Escape") cancelEditCategory();
                            }}
                            className="bg-transparent text-app-text-secondary text-sm outline-none"
                            style={{ width: `${Math.max(editingCategoryValue.length, 1)}ch` }}
                          />
                        ) : (
                          <>
                            <span className="text-app-text-secondary text-sm">{cat.label || cat}</span>
                            <button
                              onClick={() => startEditCategory(idx)}
                              className="w-4 h-4 flex items-center justify-center text-app-text-secondary hover:text-[#FEFCEB] transition-colors"
                            >
                              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                                <path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRemoveCategory(cat)}
                              className="w-4 h-4 flex items-center justify-center text-app-text-secondary hover:text-[#FEFCEB] transition-colors"
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ==================== STEP 2: VENUE ==================== */}
        {currentStep === 2 && (
          <div className="flex flex-col gap-4">
            {/* Venue Name */}
            <div className="flex flex-col gap-2">
              <label className={LABEL_CLASS}>Venue name</label>
              <input
                type="text"
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                placeholder="Venue name"
                className={INPUT_CLASS}
              />
            </div>

            {/* Location */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label className={LABEL_CLASS}>Location</label>
                <input
                  type="text"
                  value={locationName}
                  readOnly
                  placeholder="Location"
                  className={`${INPUT_CLASS} cursor-default`}
                />
              </div>

              {/* Map */}
              {isMapsLoaded && (
                <div className="relative w-full h-[320px] rounded-[20px] overflow-hidden border border-[#192634]">
                  <div className="absolute top-4 left-4 right-14 z-10">
                    <Autocomplete
                      onLoad={(ac) => { autocompleteRef.current = ac; }}
                      onPlaceChanged={() => {
                        if (autocompleteRef.current) onPlaceSelected(autocompleteRef.current.getPlace());
                      }}
                      options={{ types: ["establishment", "geocode"] }}
                    >
                      <div className="relative shadow-lg">
                        <input
                          type="text"
                          placeholder="Search location..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full bg-white text-black rounded-md pl-3 pr-8 py-2 text-sm focus:outline-none"
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                          <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                          </svg>
                        </div>
                      </div>
                    </Autocomplete>
                  </div>

                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={location || { lat: 51.5074, lng: -0.1278 }}
                    zoom={location ? 15 : 10}
                    onClick={handleMapClick}
                    onLoad={(map) => { mapRef.current = map; }}
                    options={{ disableDefaultUI: false, zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: false, styles: MAP_STYLES }}
                  >
                    {location && <Marker position={location} />}
                  </GoogleMap>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </ClubbieModal>
  );
};

export default EditCompetitionModal;
